import { useEffect, useMemo, useState } from "react";

import { getAccessToken } from "../../services/authApi";
import { fieldReader } from "../../services/loanApi";
import { getBPCPrograms } from "../../services/userApi";
import { error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";

import LoadingOverlay from "../shared/LoadingOverlay";
import Modal from "../shared/Modal";

// Existing components to reuse
import AdditionalInformation from "../commercial/AdditionalInformation";
import AdditionalInfoControl from "../urla/AdditionalInfoControl";

// Field configs
import businessAdditionalFields from "../../data/business/additionalFields.json";
import consumerAdditionalFields from "../../data/consumer/additionalFields.json";

export default function CommonAdditionalInfoReadOnlyPage() {
    const [token, setToken] = useState(null);
    const [loanid, setLoanid] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState("Loading...");
    const [fieldValues, setFieldValues] = useState({});
    const [bpcPrograms, setBpcPrograms] = useState([]);
    const [mode, setMode] = useState(""); // "BUSINESS" | "CONSUMER"
    const [showError, setShowError] = useState(false);
    const [errorTitle, setErrorTitle] = useState("Error");
    const [errorMessage, setErrorMessage] = useState("");

    const fieldsJson = useMemo(() => {
        return mode === "CONSUMER" ? consumerAdditionalFields : businessAdditionalFields;
    }, [mode]);

    useEffect(() => {
        if (!(window.elli && window.elli.script && window.elli.script.guest)) {
            console.warn("elli.script.guest is not available.");
            setLoading(false);
            return;
        }

        window.elli.script.guest.create();

        (async () => {
            try {
                const loanObject = await window.elli.script.getObject("loan");
                const raw = await loanObject.all();
                const loan = raw?.loan ?? raw;

                setLoanid(loan?.id || "");

                // Determine loan type from customFields => CX.LOAN.TYPE
                const loanTypeField = loan?.customFields?.find((f) => f.fieldName === "CX.LOAN.TYPE");
                const loanType = (loanTypeField?.value || "").toString().trim().toUpperCase();

                // If CX.LOAN.TYPE === "CONSUMER" => Consumer page, else Business
                setMode(loanType === "CONSUMER" ? "CONSUMER" : "BUSINESS");

                await getToken();
            } catch (err) {
                const errorCode = logError("COMMON_ADDLINFO_LOAN_FETCH_ERROR");
                logAppInsightError("Loan Fetch Error", {
                    component: "CommonAdditionalInfoReadOnlyPage",
                    problemId: errorCode,
                    errorCode,
                    rawError: err,
                    errorMessage: `${errorCode} - Failed to fetch loan data from guest script.`,
                });

                setErrorTitle("Loan Fetch Error");
                setErrorMessage("Failed to fetch loan from Encompass guest script.");
                setShowError(true);
                setLoading(false);
            }
        })();
    }, []);

    const getToken = async () => {
        setLoading(true);
        setLoadingText("Authorizing...");

        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const t = await getAccessToken(authCode);
            setToken(t);
        } catch (err) {
            const errorCode = logError("COMMON_ADDLINFO_TOKEN_FETCH_ERROR");
            logAppInsightError("Token Fetch Error", {
                component: "CommonAdditionalInfoReadOnlyPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch access token for loan.`,
            });

            setErrorTitle("Token Error");
            setErrorMessage("Failed to fetch access token.");
            setShowError(true);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token || !loanid || !mode) return;
        fetchFieldsReadOnly();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, loanid, mode]);

    const fetchFieldsReadOnly = async () => {
        setLoading(true);
        setLoadingText("Loading fields...");

        try {
            // Build field ids needed by both components’ internal logic
            const baseIds = (fieldsJson || []).map((f) => f.id);

            // extras used by your consumer control effects (TX + CashOut + Occupancy)
            const extraIds = [
                "TPO.X16", // Org ID for BPC programs
                "14",      // State
                "19",      // Loan Purpose
                "1811",    // Occupancy
                "LCP.X1",  // LO Comp plan
                "CX.PARENTORG.NMLS.ID",
                "CX.LOAN.PROD.BROKER.SELECT",
                "CX.DOC.TYPE",
                "TPO.X15",
                "TPO.X39",
            ];

            const allFieldIds = [...new Set([...baseIds, ...extraIds])];

            const data = await fieldReader(token, allFieldIds, loanid);
            const merged = { ...data };

            setFieldValues(merged);

            // Fetch BPC programs using ONLY OrgId from TPO.X16 (same pattern you use)
            try {
                const orgId = (merged["TPO.X16"] || "").toString().trim();
                if (orgId) {
                    const programs = await getBPCPrograms(orgId);
                    setBpcPrograms(programs);
                } else {
                    setBpcPrograms([]);
                }
            } catch (e) {
                console.error("Failed to fetch BPC Programs using OrgId (TPO.X16)", e);
                setBpcPrograms([]);
            }
        } catch (err) {
            const errorCode = logError("COMMON_ADDLINFO_FIELD_FETCH_ERROR");
            logAppInsightError("Field Fetch Error", {
                component: "CommonAdditionalInfoReadOnlyPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch field values from Encompass.`,
            });

            setErrorTitle("Field Fetch Error");
            setErrorMessage("Failed to fetch field values.");
            setShowError(true);
        } finally {
            setLoading(false);

            // Force everything read-only (no Save, no edits)
            setTimeout(() => makeFormReadOnly(), 100);
        }
    };

    const makeFormReadOnly = () => {
        const selectors = [
            'input:not([type=hidden])',
            "textarea",
            "select",
            'input[type=checkbox]',
            "button",
        ];

        const elements = document.querySelectorAll(selectors.join(", "));

        elements.forEach((el) => {
            // Ensure inputs/selects/checkboxes are disabled
            if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
                el.setAttribute("readOnly", true);
            }
            el.setAttribute("disabled", true);
            el.classList.add("cursor-not-allowed");
        });
    };

    // noop: we don’t want any “onFieldChange” to write upstream in read-only page
    const noop = () => { };

    return (
        <>
            <LoadingOverlay loading={loading} text={loadingText || "Loading..."} />

            <Modal
                title={errorTitle}
                message={errorMessage}
                isOpen={showError}
                onClose={() => setShowError(false)}
                type="error"
            />

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">
                        Additional Information
                    </h2>

                    <div className="loan-info-control-wrapper">
                        {mode === "BUSINESS" ? (
                            <AdditionalInformation
                                additionalFields={businessAdditionalFields}
                                fieldValues={fieldValues}
                                bpcPrograms={bpcPrograms}
                                onFieldChange={noop}
                            />
                        ) : (
                            <AdditionalInfoControl
                                additionalFields={consumerAdditionalFields}
                                fieldValues={fieldValues}
                                bpcPrograms={bpcPrograms}
                                hasLoCompHistory={true}
                                onFieldChange={noop}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
